import { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import type { CredentialResponse } from '@react-oauth/google';
import { GoogleLogin } from '@react-oauth/google';
import { jwtDecode } from 'jwt-decode';
import { Container, Row, Col, Card, Alert } from 'react-bootstrap';
import { useAuth } from '../../providers/AuthProvider';
import { getConfig } from '../../config';
import { Stage } from '../../config/ApplicationConfig';

interface DecodedCredential {
  email: string;
  name: string;
  picture: string;
}

interface LocationState {
  from?: {
    pathname: string;
    search?: string;
    hash?: string;
  };
}

const LoginPage = () => {
  const { isAuthenticated, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as LocationState;
  const fromPathname = state?.from?.pathname || '/';
  const fromSearch = state?.from?.search || '';
  const fromHash = state?.from?.hash || '';
  const from = `${fromPathname}${fromSearch}${fromHash}`;
  const [error, setError] = useState<string | null>(null);
  const isLocalhost = getConfig().stage === Stage.LOCAL;

  const ALLOWED_DOMAIN = 'skin-seoul.com';

  const isFirstMount = useRef(true);

  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;

      // If already authenticated, redirect to the return URL or home
      if (isAuthenticated) {
        console.log('Already authenticated, redirecting to:', from);
        navigate(from, { replace: true });
      }
    }
  }, [isAuthenticated, navigate, from]);

  const handleLoginSuccess = (response: CredentialResponse) => {
    try {
      console.log('Login successful, decoding credential');
      if (!response.credential) {
        setError('No credential received');
        return;
      }

      const decoded = jwtDecode<DecodedCredential>(response.credential);

      if (decoded && decoded.email) {
        // Check if email belongs to the allowed domain
        if (!decoded.email.endsWith(`@${ALLOWED_DOMAIN}`)) {
          setError(`Only ${ALLOWED_DOMAIN} email addresses are allowed to access this dashboard`);
          console.error('Unauthorized email domain:', decoded.email);
          return;
        }

        console.log('User authenticated:', decoded.email);
        login({
          email: decoded.email,
          name: decoded.name,
          picture: decoded.picture,
        });
        // Navigate to the page user tried to access originally
        navigate(from, { replace: true });
      }
    } catch (error) {
      console.error('Error decoding credential', error);
      setError('Failed to authenticate. Please try again.');
    }
  };

  const handleLoginError = () => {
    console.error('Google login failed');
    setError('Login failed. Please try again.');
  };

  const handleLocalhostLogin = () => {
    console.log('Localhost login');
    login({
      email: 'dev@localhost',
      name: 'Local Developer',
      picture: '',
    });
    navigate(from, { replace: true });
  };

  return (
    <Container className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
      <Row>
        <Col>
          <Card className="shadow" style={{ maxWidth: '400px' }}>
            <Card.Body className="p-5">
              <h2 className="text-center mb-4">SkinSeoul Admin</h2>
              {isLocalhost ? (
                <>
                  <p className="text-center mb-4">Development Mode</p>
                  {error && (
                    <Alert variant="danger" className="mb-4">
                      {error}
                    </Alert>
                  )}
                  <div className="d-flex justify-content-center">
                    <button className="btn btn-primary" onClick={handleLocalhostLogin}>
                      Login as Developer
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-center mb-4">Sign in with your {ALLOWED_DOMAIN} Google account</p>
                  {error && (
                    <Alert variant="danger" className="mb-4">
                      {error}
                    </Alert>
                  )}
                  <div className="d-flex justify-content-center">
                    <GoogleLogin onSuccess={handleLoginSuccess} onError={handleLoginError} useOneTap />
                  </div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default LoginPage;
